// `ScrollView` on web.
//
// The page itself scrolls, the way a website does, in every frame: a
// React Native ScrollView becomes a plain block that grows to its content,
// and the column (min-height 100vh) grows with it, so the browser's own
// scrollbar takes over. Horizontal scrollers (card rails) keep scrolling.
// The phone's bottom bar is pinned to the window by BottomNavBar.web.tsx,
// so screens keep their layout with the bar at the bottom edge.
//
// react-native-web applies the caller's `style` after its own scroll-box
// style, which is what makes this override possible without touching screens.
//
// One more thing has to give for the sheet behind the content to grow with
// it: react-native-web pins every View to `min-height: 0` (mimicking Yoga),
// so a `flex: 1` sheet inside a viewport-high column stays viewport-high and
// the content simply overflows past its bottom edge. After mount, the flex
// ancestors of a page-scrolled ScrollView get `min-height: auto`, which is
// what CSS flex does by default and lets each container size to its
// content. Absolutely positioned and horizontal-scrolling elements are
// unaffected.

import React, { useEffect, useRef } from 'react';
import { ScrollView as RNWScrollView } from 'react-native-web';

import { useFrame } from './frameContext';

type Props = React.ComponentProps<typeof RNWScrollView>;

// A screen that styles its ScrollView `flex: 1` gets `flex-basis: 0%` from
// react-native-web; with growth turned off that basis makes the box zero
// height, so its content spills over whatever comes after it (the "Save
// Changes" button sat on top of the list). Basis auto = the content's height.
const docScroll = { overflow: 'visible' as const, flexGrow: 0, flexShrink: 0, flexBasis: 'auto' as const };

function relaxAncestors(node: HTMLElement | null): void {
  let el = node?.parentElement ?? null;
  while (el && el !== document.body) {
    const computed = getComputedStyle(el);
    if (
      (el.style.minHeight === '' || el.style.minHeight === '0px') &&
      computed.minHeight === '0px' &&
      computed.display === 'flex'
    ) {
      el.style.minHeight = 'auto';
    }
    // React Navigation's web stack lays its screens out in a reversed
    // column. Once a screen grows past the window it would anchor to the
    // bottom and push its top above the viewport, where no scroll can reach
    // it. In a reversed column, `flex-end` is the top.
    if (computed.flexDirection === 'column-reverse' && el.style.justifyContent === '') {
      el.style.justifyContent = 'flex-end';
    }
    el = el.parentElement;
  }
}

// With the page doing the scrolling, the box a screen holds a ref to never
// scrolls, so `scrollToEnd()` / `scrollTo()` did nothing: Orion's tutorial
// and chat append a message and call `scrollToEnd`, and on the web the new
// message and the button under it stayed below the window. The imperative
// calls are pointed at the page instead. `scrollTo({ y })` is relative to
// the top of the box, as it is on the phone.
type ScrollArgs = { x?: number; y?: number; animated?: boolean } | number | undefined;

function redirectScrollToPage(instance: RNWScrollView): void {
  const target = instance as unknown as {
    __fkPageScroll?: boolean;
    getScrollableNode?: () => HTMLElement | null;
    scrollTo: (a?: ScrollArgs, b?: number, c?: boolean) => void;
    scrollToEnd: (a?: { animated?: boolean }) => void;
  };
  if (target.__fkPageScroll) return;
  target.__fkPageScroll = true;
  const behavior = (animated?: boolean): ScrollBehavior => (animated === false ? 'auto' : 'smooth');
  target.scrollToEnd = (a) => {
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: behavior(a?.animated) });
  };
  target.scrollTo = (a, b, c) => {
    const y = typeof a === 'number' ? a : a?.y ?? 0;
    const animated = typeof a === 'number' ? c : a?.animated;
    void b;
    const node = target.getScrollableNode?.();
    const boxTop = node ? node.getBoundingClientRect().top + window.scrollY : 0;
    window.scrollTo({ top: Math.max(0, boxTop + y), behavior: behavior(animated) });
  };
}

export const ScrollView = React.forwardRef<RNWScrollView, Props>(function ScrollView(props, ref) {
  const frame = useFrame();
  const inner = useRef<RNWScrollView | null>(null);
  const usePageScroll = !props.horizontal;

  const setRefs = (instance: RNWScrollView | null) => {
    if (instance && usePageScroll) redirectScrollToPage(instance);
    inner.current = instance;
    if (typeof ref === 'function') ref(instance);
    else if (ref) (ref as React.MutableRefObject<RNWScrollView | null>).current = instance;
  };

  useEffect(() => {
    if (!usePageScroll) return;
    const instance = inner.current as unknown as { getScrollableNode?: () => HTMLElement } | null;
    const node = instance?.getScrollableNode?.() ?? null;
    relaxAncestors(node);
  }, [usePageScroll, frame]);

  if (!usePageScroll) return <RNWScrollView ref={setRefs} {...props} />;
  return <RNWScrollView ref={setRefs} {...props} style={[props.style, docScroll]} />;
});

export default ScrollView;
